import axios from "axios";
import { Ticket, Sun, Headphones, MonitorPlay, CalendarCheck } from "lucide-react";

export const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
api.interceptors.request.use((c) => {
  const t = localStorage.getItem("token");
  if (t) c.headers.Authorization = `Bearer ${t}`;
  return c;
});
api.interceptors.response.use(
  (r) => r,
  (e) => {
    if (e.response?.status === 401 && !e.config.url.includes("/auth/login") && !e.config.url.includes("/auth/google")) {
      localStorage.removeItem("token");
      if (window.location.pathname !== "/login") window.location.href = "/login";
    }
    return Promise.reject(e);
  }
);
export const errMsg = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x.msg).join(" ");
  return "Algo ha fallado. Inténtalo de nuevo.";
};

export const TIPOS_CITA = {
  evaluacion: { label: "Evaluación", color: "#1E3A8A" },
  revision: { label: "Revisión", color: "#0284C7" },
  sesion_tv: { label: "Sesión TV", color: "#4338CA" },
  syntonic: { label: "Syntonic", color: "#D97706" },
  wivi: { label: "Wivi", color: "#059669" },
  tomatis: { label: "Tomatis", color: "#0891B2" },
  bloqueo: { label: "Bloqueo / no dar cita", color: "#94A3B8" },
  otro: { label: "Otro", color: "#9333EA" },
};
export const ESTADOS_CITA = { programada: "Programada", confirmada: "Confirmada", atendida: "Atendida", anulada: "Anulada", no_vino: "No vino" };
export const textOn = (hex) => {
  const h = (hex || "#64748B").replace("#", "");
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16));
  return 0.299 * r + 0.587 * g + 0.114 * b > 160 ? "#1E293B" : "#FFFFFF";
};
export const HUECOS_TIPO = {
  evaluacion: { label: "Hueco evaluación", cita: "evaluacion", color: "#1E3A8A" },
  revision: { label: "Hueco revisión", cita: "revision", color: "#0284C7" },
  terapia: { label: "Hueco terapia", cita: "sesion_tv", color: "#4338CA" },
  optica: { label: "Hueco óptica", cita: "revision", color: "#0F766E" },
  libre: { label: "Hueco libre", cita: "otro", color: "#059669" },
  comida: { label: "Comida", cita: "bloqueo", color: "#94A3B8" },
  no_agendar: { label: "No agendar", cita: "bloqueo", color: "#DC2626" },
};
export const FRECUENCIAS = [
  { v: 0, label: "No se repite" },
  { v: 1, label: "Cada semana" },
  { v: 2, label: "Cada 15 días" },
  { v: 4, label: "Cada 4 semanas (mensual)" },
  { v: 3, label: "Cada 3 semanas" },
];

export const waLink = (tel, msg) => {
  let n = (tel || "").replace(/\D/g, "");
  if (n.length === 9) n = `34${n}`;
  return `https://wa.me/${n}?text=${encodeURIComponent(msg)}`;
};

export const CENTROS = ["MADRID", "ALCOBENDAS"];
export const OPTOS = ["LAURA", "NATALIA", "PILAR"];

export const ETAPAS = [
  { id: "evaluacion", label: "Nuevos (sin evaluar)", cls: "bg-slate-100 text-slate-700 border-slate-300" },
  { id: "informe", label: "Informe", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  { id: "presupuesto", label: "Presupuesto", cls: "bg-violet-50 text-violet-700 border-violet-200" },
  { id: "respuesta", label: "Esperando respuesta", cls: "bg-amber-50 text-amber-800 border-amber-200" },
  { id: "tratamiento", label: "En tratamiento", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  { id: "alta", label: "Alta", cls: "bg-teal-50 text-teal-700 border-teal-200" },
  { id: "baja", label: "Baja", cls: "bg-rose-50 text-rose-700 border-rose-200" },
  { id: "anulada", label: "Cita anulada", cls: "bg-orange-50 text-orange-700 border-orange-200" },
];
export const etapaInfo = (id) => ETAPAS.find((e) => e.id === id) || ETAPAS[0];

export const TIPOS = {
  bono: { label: "Bonos", single: "Bono TV", icon: Ticket, cls: "bg-indigo-50 text-indigo-700 border-indigo-200", dot: "#4338CA" },
  syntonic: { label: "Syntonic", single: "Syntonic", icon: Sun, cls: "bg-amber-50 text-amber-800 border-amber-200", dot: "#D97706" },
  tomatis: { label: "Tomatis", single: "Tomatis", icon: Headphones, cls: "bg-sky-50 text-sky-700 border-sky-200", dot: "#0284C7" },
  wivi: { label: "Wivi", single: "Wivi", icon: MonitorPlay, cls: "bg-emerald-50 text-emerald-700 border-emerald-200", dot: "#059669" },
  sesiones: { label: "Sesiones sin bono", single: "Sesiones", icon: CalendarCheck, cls: "bg-rose-50 text-rose-700 border-rose-200", dot: "#BE185D" },
};

export const ESTADOS_TTO = [
  { id: "pendiente", label: "Aprobado, sin empezar", cls: "bg-amber-50 text-amber-800 border-amber-200" },
  { id: "en_proceso", label: "En tratamiento", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  { id: "finalizado", label: "Finalizado", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  { id: "baja", label: "Baja", cls: "bg-rose-50 text-rose-700 border-rose-200" },
];
export const estadoInfo = (id) => ESTADOS_TTO.find((e) => e.id === id) || ESTADOS_TTO[0];

export const SUGERIDOS = [
  { id: "bono", label: "Bono 14 sesiones (semanal)" },
  { id: "bono_trimestral", label: "Bono trimestral con programa casa (6 sesiones)" },
  { id: "programa_casa", label: "Programa de casa" },
  { id: "sesion_mensual", label: "Mensualidad (sesión semanal)" },
  { id: "sesion_quincenal", label: "Sesión quincenal" },
  { id: "sesion_suelta", label: "Sesión suelta" },
  { id: "syntonic", label: "Syntonic" },
  { id: "wivi", label: "Wivi" },
  { id: "wivi_lectura", label: "Wivi lectura" },
  { id: "kit_eficacia", label: "Kit eficacia" },
  { id: "kit_procesamiento", label: "Kit procesamiento" },
  { id: "tomatis", label: "Tomatis" },
  { id: "gafas", label: "Gafas" },
  { id: "revision", label: "Revisión" },
];

export const OPC = {
  tipo_examen: ["BASICO", "EFICACIA", "EE Y PV", "REVISION", "REV EE", "REV PV", "PERCEPCION VISUAL", "REV PROTOCOLO DE ALTA", "RECOMENDACIONES PROFESOR"],
  necesita_pv: ["SI", "NO", "LATERALIDAD"],
  tipo_informe: ["BASICO", "NO NECESITA", "EFICACIA", "PERCEPCION VISUAL", "AMBOS", "AMBOS, AUDITIVO", "REV EE", "REV EE, AUDITIVO", "REV PV", "ORTO K", "RECOMENDACIONES PROFESOR"],
  estado_informe: ["POR HACER", "EMPEZADO", "POR LEER LAURA", "POR ENVIAR", "ENVIADO", "NO NECESITA", "ANULADO"],
  envio_ppto: ["PENDIENTE", "ENVIADO", "ENTREGADO EN MANO", "NO NECESITA", "ANULADO"],
  respuesta: ["SIN CONTESTAR", "APROBADO", "NO APROBADO"],
  momento_rev: ["INTERMEDIA", "FINAL", "ANUAL", "HACE TIEMPO QUE NO VIENE"],
  eval_auditiva: ["POR HACER", "HECHA", "NO NECESITA"],
  nuevo_tto: ["SI", "NO"],
  prox_revision_en: ["1 MES", "3 MESES", "6 MESES", "1 AÑO"],
  respuesta_oido: ["SIN CONTESTAR", "APROBADO", "NO APROBADO", "NO NECESITA", "REVISION EN 3 MESES"],
  hora_terapia: ["SIN CONTESTAR", "HORA SIN CONFIRMAR", "SIN HORA PERO APROBADO", "HORA CONFIRMADA", "NO NECESITA", "BAJA"],
  economico: ["PENDIENTE", "SI", "NO"],
  estado_cita: ["SIN CITA", "CITA DADA", "ANULADA"],
  motivo_anulacion: ["Precio", "Horario", "Enfermedad", "Distancia", "Se lo piensa", "Va a otro centro", "No contesta", "Otro"],
  quien_remite: ["ANTIGUO PACIENTE", "NUEVO", "BARRIO", "IDEO", "SAMAY", "OFTALMOLOGO", "TERAPEUTA OCUPACIONAL", "AMIGO"],
  modalidad: ["BONO 14 SESIONES", "BONO TRIMESTRAL CON PROGRAMA CASA", "BONO TRATAMIENTO COMPLETO"],
  modalidad_ses: ["SESIÓN QUINCENAL", "MENSUALIDAD (SEMANAL)", "SESIÓN SUELTA"],
  aparato: ["SILVIA 1", "SILVIA 2", "SILVIA 3", "SILVIA 4", "SILVIA 5", "SILVIA 6", "COLIER 1", "COLIER 2", "COLIER 3", "COLIER 4", "COLIER 5", "COLIER 6"],
  metodo_pago: ["Efectivo", "Tarjeta", "Transferencia", "Bizum", "Financiado"],
};

const S = {
  ok: { background: "#ECFDF5", color: "#047857", borderColor: "#A7F3D0" },
  wait: { background: "#FFFBEB", color: "#92400E", borderColor: "#FDE68A" },
  work: { background: "#EFF6FF", color: "#1D4ED8", borderColor: "#BFDBFE" },
  bad: { background: "#FFF1F2", color: "#BE123C", borderColor: "#FECDD3" },
  na: { background: "#F8FAFC", color: "#64748B", borderColor: "#E2E8F0" },
};
const STATUS = {
  ok: ["ENVIADO", "APROBADO", "HORA CONFIRMADA", "SI", "ENTREGADO EN MANO", "CITA DADA", "HECHA"],
  wait: ["POR LEER LAURA", "LEIDO LAURA/PENDIENTE OIDO", "PENDIENTE", "SIN CONTESTAR", "HORA SIN CONFIRMAR", "SIN HORA PERO APROBADO", "POR ENVIAR", "SIN CITA"],
  work: ["POR HACER", "EMPEZADO", "REVISION", "REVISION EN 3 MESES", "GAFAS"],
  bad: ["ANULADO", "NO APROBADO", "BAJA", "NO", "ANULADA"],
  na: ["NO NECESITA"],
};
export const statusStyle = (v) => {
  const u = (v || "").trim().toUpperCase();
  const k = Object.keys(STATUS).find((key) => STATUS[key].includes(u));
  return k ? S[k] : null;
};

export const PEND_LABELS = {
  sin_cita: "Llamó sin coger cita",
  ee_nuevos: "Exámenes de eficacia nuevos",
  revisiones: "Revisiones (antiguos, en tratamiento y alta)",
  ppto_rev_por_enviar: "Presupuesto de revisión por enviar",
  revision_sin_aprobar: "Revisión sin aprobar",
  revision_final_alta: "Revisión final: decidir alta",
  auditiva_por_hacer: "Evaluación auditiva por hacer",
  prox_revision: "Próxima revisión por citar",
  ppto_pv_por_enviar: "Presupuesto PV por enviar",
  respuesta_pv_sin_contestar: "Respuesta PV sin contestar",
  pv_sin_fecha: "PV aprobada sin cita",
  informe_por_hacer: "Informe por hacer",
  informe_por_leer: "Informe por leer",
  informe_por_enviar: "Informe por enviar",
  ppto_por_enviar: "Presupuesto por enviar",
  respuesta_sin_contestar: "Respuesta sin contestar",
  oido_sin_contestar: "Oído sin contestar",
  sin_hora: "Aprobado sin hora confirmada",
  economico_pendiente: "Económico pendiente",
  valorar_siguiente: "Valorar siguiente tratamiento",
};
export const BONO_AVISO = 3;
export const altaMsg = (p, r) => `Hola, os escribimos de Salud y Visión. Le toca a ${p.nombre} su ${(r.etiqueta || "revisión").toLowerCase().replace(" + informe final", "")} tras el alta, hacia el ${r.fecha ? r.fecha.split("-").reverse().join("/") : ""}. ¿Qué día y hora os viene bien para darle cita? Gracias.`;
export const recogidaTomatisMsg = (nombre, ciclo, fecha) => `Hola, os escribimos de Salud y Visión. ${nombre} empieza su ciclo ${ciclo} de Tomatis${fecha ? ` el ${fecha.split("-").reverse().join("/")}` : ""}. Ese día podéis pasar por el centro a recoger el aparato. Si os viene mejor otro día, decídnoslo. Gracias.`;
export const devolucionTomatisMsg = (nombre, ciclo, fecha) => `Hola, os escribimos de Salud y Visión. ${nombre} termina su ciclo ${ciclo} de Tomatis${fecha ? ` el ${fecha.split("-").reverse().join("/")}` : ""}. Ese día, por favor, pasad por el centro a devolver el aparato con todos sus accesorios. Si no podéis ese día, avisadnos. Gracias.`;
export const valorarMsg = (p) => {
  const t = (p.tratamientos_valorar || []).map((x) => SUGERIDOS.find((s) => s.id === x)?.label.toLowerCase() || x).join(", ");
  if (!p.valorar_pendiente && p.valorar_volver) {
    return `Hola, os escribimos de Salud y Visión. Hace unas semanas hablamos del siguiente paso para ${p.nombre} tras el programa Syntonic${t ? ` (${t})` : ""}. ¿Lo habéis podido pensar? Si queréis, os damos cita para valorarlo juntos. Gracias.`;
  }
  return `Hola, os escribimos de Salud y Visión. ${p.nombre} ya ha terminado el programa Syntonic. Nos gustaría daros cita para valorar juntos cómo ha ido y el siguiente paso${t ? ` (${t})` : ""}. ¿Qué día y hora os vendría bien? Gracias.`;
};
export const esRevision = (f) => ["REV EE", "REV PV", "REVISION"].includes((f.tipo_examen || "").toUpperCase());
export const soloRevision = (f) => esRevision(f) && (f.nuevo_tto || "").toUpperCase() !== "SI";
export const TRIMESTRAL = "BONO TRIMESTRAL CON PROGRAMA CASA";
export const avisoSiguiente = (data, toast, nav) => {
  if (!data.siguiente) return;
  const s = data.siguiente;
  toast.info(`Siguiente en espera: ${s.nombre}${s.centro ? ` (${s.centro})` : ""}${data.aparato_devuelto ? ` · darle el ${data.aparato_devuelto}` : ""}`,
    { duration: 15000, action: { label: "Ver", onClick: () => nav(`/tratamiento/${s.treatment_id}`) } });
};

export const edadDe = (fn) => {
  if (!fn) return null;
  const b = new Date(fn), t = new Date();
  let a = t.getFullYear() - b.getFullYear();
  if (t.getMonth() < b.getMonth() || (t.getMonth() === b.getMonth() && t.getDate() < b.getDate())) a -= 1;
  return a >= 0 ? a : null;
};
export const edadTexto = (p) => {
  const a = edadDe(p.fecha_nacimiento);
  return a !== null ? `${a} años` : p.edad || "";
};

export const fmtDate = (d) => {
  if (!d) return "—";
  const [y, m, day] = d.slice(0, 10).split("-");
  return `${day}/${m}/${y}`;
};
export const fmtEur = (n) => `${Number(n || 0).toLocaleString("es-ES", { minimumFractionDigits: 0, maximumFractionDigits: 2 })} €`;
export const waPhone = (t) => {
  const d = (t || "").replace(/\D/g, "");
  return d.length === 9 ? `34${d}` : d;
};
export const fmtEur2 = (n) => `${Number(n || 0).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
export const today = () => new Date().toISOString().slice(0, 10);
export const newItem = (etiqueta = "", extra = {}) => ({ id: crypto.randomUUID(), etiqueta, fecha: null, realizada: false, nota: "", ...extra });
